import { useMemo } from 'react'

import {
  useHasFreeEstimates,
  useGetCancelReasonsOnMount,
  useSchedulingEnabled,
} from '@user/store/scheduling'
import {
  useSelectFunnelStagesByType,
  useGetEntityCloseReasonsOnMount,
} from '@user/store/salesFunnel'
import { ENTITY_TYPES, SOLD_SERVICE_STATUS } from '@sales-center/common'
import { useAppSelector } from '@user/store/hooks'
import { selectAllCancelReasons } from '@user/store/scheduling/cancelReasonsSlice'

type Props = {
  entityId: number
  salesFunnelId: number
  entityType: ENTITY_TYPES
  soldServiceStatus?: SOLD_SERVICE_STATUS
}

export function useData(props: Props) {
  const { entityId, entityType, salesFunnelId, soldServiceStatus } = props

  const { status, reasonDetail } = useGetEntityCloseReasonsOnMount({
    entityType,
    salesFunnelId,
  })

  const { loading: estimatesLoading, hasFreeEstimates } = useHasFreeEstimates({
    entityId,
    entityType,
    soldServiceStatus,
  })

  const schedulingEnabled = useSchedulingEnabled()
  const cancelReasons = useAppSelector(selectAllCancelReasons)
  const cancelReasonStatus = useGetCancelReasonsOnMount(schedulingEnabled)

  const stages = useSelectFunnelStagesByType(
    salesFunnelId,
    entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity',
  )

  const stageId = useMemo(() => {
    const search =
      entityType === ENTITY_TYPES.Lead ? 'Disqualified' : 'Closed/Lost'

    return stages.find((s) => s.closed && s.name === search)?.id ?? -1
  }, [entityType, stages])

  const loading =
    status === 'pending' || cancelReasonStatus === 'pending' || estimatesLoading

  return {
    status,
    stageId,
    loading,
    reasonDetail,
    cancelReasons,
    hasFreeEstimates,
  }
}
